import { observer } from 'mobx-react-lite';
import { Box, Form, getVariant, spacing } from '@apia/theme';
import {
  ChangeEvent,
  ClipboardEvent as ReactClipboardEvent,
  useRef,
  useState,
} from 'react';
import { getLabel } from '@apia/util';
import { IAttachment, TChatModelOption } from '../types';
import { getBase64FromFile } from '../util';
import {
  ApiaUtil,
  AutogrowTextarea,
  IconButton,
  TMenuItem,
} from '@apia/components';
import { ChatMessage } from './ChatMessage';
import { PendingMessagesPanel } from './PendingMessagesPanel';

function getImageExtension(mimeType: string) {
  const type = mimeType.toLowerCase();

  if (type === 'image/jpeg') return 'jpg';
  if (type === 'image/svg+xml') return 'svg';
  return type.startsWith('image/') ? type.slice('image/'.length) : 'png';
}

function makePastedImageName(mimeType: string) {
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\..+/, '')
    .replace('T', '_');

  return `pasted_image_${timestamp}.${getImageExtension(mimeType)}`;
}

function normalizePastedImageFile(file: File) {
  if (file.name) {
    return file;
  }

  return new File([file], makePastedImageName(file.type), {
    lastModified: Date.now(),
    type: file.type,
  });
}

function getPastedImageFiles(clipboardData: DataTransfer) {
  const itemFiles = Array.from(clipboardData.items)
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file instanceof File);

  const files = itemFiles.length > 0
    ? itemFiles
    : Array.from(clipboardData.files).filter((file) =>
        file.type.startsWith('image/'),
      );

  return files
    .map(normalizePastedImageFile);
}

export const TextArea = observer(
  ({
    additionalOptions = [],
    isLoading,
    isStopping,
    isRecording,
    disablePrevButton,
    disableNextButton,
    isModelSelectionLoading,
    onSubmit,
    onStop,
    onChange,
    onChangeModel,
    onClearHistory,
    onAttach,
    onRecord,
    onRecordCancel,
    onVideoRecord,
    onPendingMessagesHeightChange,
    onPrev,
    onNext,
    onRemovePendingMessage,
    value,
    attachments,
    allowModelSelection,
    canAttach,
    canNavigateHistory,
    canOpenCamera,
    canRecord,
    className,
    modelOptions,
    pendingMessages,
    selectedModel,
    onUpdatePendingMessage,
  }: {
    additionalOptions?: TMenuItem[];
    allowModelSelection: boolean;
    canAttach: boolean;
    canNavigateHistory: boolean;
    canOpenCamera: boolean;
    canRecord: boolean;
    className?: string;
    isLoading: boolean;
    isStopping: boolean;
    isModelSelectionLoading: boolean;
    isRecording: boolean;
    disablePrevButton: boolean;
    disableNextButton: boolean;
    onClearHistory: () => unknown;
    onSubmit: () => unknown;
    onStop?: () => unknown;
    onChange: (v: string) => unknown;
    onChangeModel: (model: string) => unknown;
    onAttach: (ev: IAttachment) => unknown;
    onRecord: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onRecordCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onPendingMessagesHeightChange: (height: number) => unknown;
    onRemovePendingMessage: (message: ChatMessage) => unknown;
    onVideoRecord: () => unknown;
    onUpdatePendingMessage: (message: ChatMessage, value: string) => unknown;
    onPrev: () => unknown;
    onNext: () => unknown;
    modelOptions: TChatModelOption[];
    pendingMessages: ChatMessage[];
    selectedModel?: string;
    value: string;
    attachments: IAttachment[];
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const searchControlsRef = useRef<HTMLTextAreaElement>(null);

    const [dragging, setDragging] = useState(false);
    const attachFiles = async (files: Iterable<File>) => {
      for (const f of files) {
        const base64 = await getBase64FromFile(f);
        onAttach({
          base64,
          name: f.name,
          id: f.name,
        });
      }
    };

    const handleAttachments = async (ev: ChangeEvent<HTMLInputElement>) => {
      if (ev.target.files) {
        await attachFiles(Array.from(ev.target.files));
        ev.target.value = '';
      }
    };

    const handlePaste = async (ev: ReactClipboardEvent<HTMLTextAreaElement>) => {
      const files = getPastedImageFiles(ev.clipboardData);

      if (files.length === 0) {
        return;
      }

      ev.preventDefault();
      await attachFiles(files);
    };

    const menuItemsList: TMenuItem[] = [];
    const addMenuSection = (items: TMenuItem[]) => {
      if (items.length === 0) {
        return;
      }

      if (
        menuItemsList.length > 0 &&
        menuItemsList[menuItemsList.length - 1] !== 'separator'
      ) {
        menuItemsList.push('separator');
      }

      menuItemsList.push(...items);
    };

    const attachmentItems: TMenuItem[] = [];
    if (canAttach) {
      attachmentItems.push({
        children: getLabel('btnUpload').text,
        title: getLabel('btnUpload').text,
        icon: 'File',
        onClick: () => fileInputRef.current?.click(),
        key: '2',
      });
    }
    if (canAttach && canOpenCamera) {
      attachmentItems.push({
        title: getLabel('lblOpenCamera').text,
        children: getLabel('lblOpenCamera').text,
        icon: 'Camera',
        onClick: onVideoRecord,
        key: '3',
      });
    }

    const historyItems: TMenuItem[] = [];
    if (canNavigateHistory) {
      historyItems.push(
        {
          icon: 'ArrowLeft',
          children: getLabel('btnAiPrevious').text,
          title: getLabel('btnAiPrevious').text,
          onClick: onPrev,
          disabled: disablePrevButton,
          key: '0',
        },
        {
          icon: 'ArrowRight',
          children: getLabel('btnAiNext').text,
          title: getLabel('btnAiNext').text,
          onClick: onNext,
          disabled: disableNextButton,
          key: '1',
        },
      );
    }

    const modelItems: TMenuItem[] = [];
    if (allowModelSelection && modelOptions.length > 0) {
      modelItems.push({
        children: 'Modelos disponibles',
        disabled: true,
        key: 'model-selection-header',
        title: 'Modelos disponibles',
      });

      modelItems.push(
        ...modelOptions.map((model) => ({
          children: model.label,
          disabled: isModelSelectionLoading,
          icon: model.id === selectedModel ? ('Check' as const) : undefined,
          key: `model-selection-${model.id}`,
          onClick: () => {
            if (model.id !== selectedModel) {
              onChangeModel(model.id);
            }
          },
          title: model.label,
        })),
      );
    }

    addMenuSection(additionalOptions);
    addMenuSection(attachmentItems);
    addMenuSection(modelItems);
    addMenuSection(historyItems);
    addMenuSection([
      {
        icon: 'Trash',
        onClick: onClearHistory,
        key: '5',
        children: getLabel('btnAiDeleteMessages').text,
        title: getLabel('btnAiDeleteMessages').text,
      },
    ]);
    const canSubmitCurrentMessage =
      !isModelSelectionLoading && (value !== '' || attachments.length > 0);
    const showRecordButton =
      canRecord &&
      !isLoading &&
      !isRecording &&
      value === '' &&
      attachments.length === 0;
    const handleDragLeave = (ev: React.DragEvent<HTMLElement>) => {
      ev.preventDefault();
      setDragging(false);
    };

    const handleDrop = async (ev: React.DragEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setDragging(false);
      await attachFiles(Array.from(ev.dataTransfer.files));
    };

    const handleDragOver = (ev: React.DragEvent<HTMLElement>) => {
      ev.preventDefault();
    };

    const handleDragEnter = (ev: React.DragEvent<HTMLElement>) => {
      ev.preventDefault();
      setDragging(true);
    };
    return (
      <Form
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onSubmit={(ev) => {
          ev.preventDefault();
          onSubmit();
        }}
        className={`search_controls ${className || ''}`}
        {...getVariant('layout.common.chat.form')}
      >
        {dragging ? (
          <Box
            sx={{
              height: '150px',
              border: '3px dashed #aaa',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getLabel('msgAiDragAndDrop').text}
          </Box>
        ) : (
          <>
            <PendingMessagesPanel
              messages={pendingMessages}
              onHeightChange={onPendingMessagesHeightChange}
              onRemoveMessage={onRemovePendingMessage}
              onUpdateMessage={onUpdatePendingMessage}
            />
            <Box className="chatInputRow">
              <AutogrowTextarea
                ref={searchControlsRef}
                aria-label={
                  /** TODO: Crear etiqueta para búsqueda */ 'Frase de búsqueda'
                }
                value={value}
                className="searchTextArea"
                onChange={(ev) => onChange(ev.target.value)}
                onPaste={handlePaste}
                onKeyDown={(ev) => {
                  if (
                    (ev.code === 'Enter' || ev.code === 'NumpadEnter') &&
                    !ev.shiftKey
                  ) {
                    ev.preventDefault();
                    (ev.target as HTMLElement)
                      .closest('form')!
                      .querySelector<HTMLElement>('[type="submit"]')!
                      .click();
                  }
                }}
                sx={{
                  resize: 'none',
                  px: spacing(4),
                  background: 'transparent',
                  border: 'none',
                  alignContent: 'center',
                }}
              />
              <Box className="chatButtons">
                <IconButton
                  className="ChatBarButton"
                  icon={isRecording ? 'Trash' : 'Ellipsis'}
                  variant="icon-only"
                  iconSize="Sm"
                  onClick={
                    isRecording
                      ? onRecordCancel
                      : (ev) => {
                          ApiaUtil.instance.menu.open({
                            menuProps: {
                              anchorPoint: { x: ev.clientX, y: ev.clientY },
                            },
                            items: menuItemsList,
                          });
                        }
                  }
                ></IconButton>
                {isLoading && !isRecording && onStop && (
                  <IconButton
                    className="ChatBarButton"
                    icon="Stop"
                    variant="icon-only"
                    iconSize="Sm"
                    title={getLabel('btnCan').text}
                    onClick={onStop}
                    isLoading={isStopping}
                    disabled={isStopping}
                    sx={{
                      color: 'palette.error.main',
                    }}
                  />
                )}
                {showRecordButton ? (
                  <IconButton
                    className={`ChatBarButton ${isRecording ? 'isRecording' : ''}`}
                    icon={isRecording ? 'Stop' : 'Microphone'}
                    variant="icon-only"
                    iconSize="Sm"
                    title={getLabel('lblAiRecordAudio').text}
                    onClick={onRecord}
                    isLoading={isRecording ? false : isLoading}
                    disabled={isLoading && !isRecording}
                    sx={{
                      color: isRecording ? 'red' : undefined,
                    }}
                    id="transcribeAudio"
                  />
                ) : (
                  <IconButton
                    className="ChatBarButton"
                    disabled={!canSubmitCurrentMessage && !isRecording}
                    icon="ArrowRight"
                    variant="icon-only"
                    iconSize="Sm"
                    title={getLabel('btnBus').title}
                    type="submit"
                  />
                )}
              </Box>
            </Box>
          </>
        )}
        <input
          ref={fileInputRef}
          id="invisible__attach__file"
          type="file"
          multiple
          onChange={handleAttachments}
          sx={{ display: 'none' }}
        ></input>
      </Form>
    );
  },
);
